/**
 * Client-side helpers for the recipe PDF import upload flow (pdf-import,
 * Slice 3b — `PdfImportButton`). Mirrors `videoUpload.ts`'s shape: there is
 * exactly one accepted content type (`application/pdf`), so validation is
 * simpler — a file is either a PDF under the size cap, or it's rejected
 * before ever attempting an upload.
 *
 *  - `PDF_MAX_BYTES` — mirrors the API's own cap (`PDF_MAX_BYTES` in
 *    `recipe-pdf-import.service.ts`, 40 MB) so an oversized file is rejected
 *    client-side with an immediate, clear message instead of burning an
 *    upload round-trip only to fail server-side.
 *  - `looksLikePdfFile` — a browser can report a blank `file.type` for some
 *    PDF files; falls back to a filename-extension check.
 *  - `validatePdfFile` — reject (before any upload attempt) a file that
 *    isn't recognizable as a PDF, or that's over the size cap.
 */

/** The only content type the API's pdf upload-url endpoint accepts. */
export const PDF_CONTENT_TYPE = "application/pdf";

/** Reject anything picked over this size, before even attempting an upload —
 * mirrors the API's own `PDF_MAX_BYTES` cap (40 MB). */
export const PDF_MAX_BYTES = 40 * 1024 * 1024;

/** Resolve whether a picked file looks like a PDF: the browser-reported
 * `file.type` if present, else a best-effort guess from the filename's
 * extension. */
export function looksLikePdfFile(file: File): boolean {
  if (file.type) return file.type === PDF_CONTENT_TYPE;
  return file.name.toLowerCase().endsWith(".pdf");
}

/**
 * Validate a picked PDF file BEFORE attempting any upload. Returns a short
 * Norwegian error message on failure, or `null` when the file is acceptable.
 */
export function validatePdfFile(file: File): string | null {
  if (!looksLikePdfFile(file)) {
    return "Filtypen støttes ikke. Bruk en PDF-fil.";
  }
  if (file.size > PDF_MAX_BYTES) {
    return "PDF-en er for stor (maks 40 MB).";
  }
  return null;
}
